import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import {
  isLikelyFleetEmbed,
  isTrustedFleetParentMessage,
  setFleetEmbedInStorage,
} from 'app/utils/fleet-embed';
import { performFleetEmbedLogout } from 'app/utils/fleet-logout';

type FleetEmbedContextValue = {
  isFleetEmbed: boolean;
};

const FleetEmbedContext = createContext<FleetEmbedContextValue>({
  isFleetEmbed: false,
});

function isFleetLogoutPayload(data: unknown): boolean {
  if (data === 'logout') {
    return true;
  }
  if (typeof data === 'string' && data.toLowerCase() === 'logout') {
    return true;
  }
  if (data && typeof data === 'object') {
    const record = data as {
      type?: unknown;
      action?: unknown;
      event?: unknown;
    };
    return (
      record.type === 'logout' ||
      record.action === 'logout' ||
      record.event === 'logout'
    );
  }
  return false;
}

/**
 * Single App-level listener for Fleet iframe postMessage contracts.
 * Trusts the embedding parent (any host) — not a fixed origin allowlist.
 */
export function FleetEmbedProvider({ children }: { children: ReactNode }) {
  const [isFleetEmbed, setIsFleetEmbed] = useState(() => isLikelyFleetEmbed());
  const logoutRef = useRef(performFleetEmbedLogout);
  logoutRef.current = performFleetEmbedLogout;

  useEffect(() => {
    if (isLikelyFleetEmbed()) {
      setIsFleetEmbed(true);
    }

    const onMessage = (event: MessageEvent) => {
      const isLogout = isFleetLogoutPayload(event.data);
      const isHideElements = event.data === 'hideElements';

      if (!isLogout && !isHideElements) {
        return;
      }

      if (!isTrustedFleetParentMessage(event)) {
        console.warn('[ELD] Ignoring Fleet-like postMessage (not from parent)', {
          origin: event.origin,
          data: event.data,
        });
        return;
      }

      console.log('[ELD] Fleet postMessage from parent', {
        origin: event.origin,
        data: event.data,
      });

      if (isHideElements) {
        setFleetEmbedInStorage(true);
        setIsFleetEmbed(true);
        return;
      }

      console.log('[ELD] Fleet logout — clearing ELD session');
      logoutRef.current();
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const value = useMemo(() => ({ isFleetEmbed }), [isFleetEmbed]);

  return (
    <FleetEmbedContext.Provider value={value}>
      {children}
    </FleetEmbedContext.Provider>
  );
}

export function useFleetEmbedContext(): FleetEmbedContextValue {
  return useContext(FleetEmbedContext);
}
